import { useState } from "react";
import * as React from "react";

import dayjs from "dayjs";
import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Divider,
  DialogActions,
  Switch,
  Box,
  Autocomplete,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";

import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { openSnackbar } from "../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../api";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SnackbarProps } from "../../../../../types/snackbar";
import TextArea from "antd/es/input/TextArea";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Currency,
  JobType,
  ResumeExperience,
} from "../../../../../types/resumes-managment/resume";
import { useFormik, FormikProvider, Form } from "formik";

import { Country, City } from "../../../../../types/site-params/Country";
import { Category } from "../../../../../types/site-params/Category";
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";
import { useCategoriesForAutocomplete } from "../../../../../hooks/query-hooks/useCategories";
import { useJobTypesForAutocomplete } from "../../../../../hooks/query-hooks/useJobTypes";
import { useCurrenciesForAutocomplete } from "../../../../../hooks/query-hooks/useCurrencies";
import { useDebounce } from "../../../../../hooks";

const getInitialValues = (experience: ResumeExperience | null) => {
  if (experience && experience.id) {
    return {
      job_title: experience.job_title || "",
      company_name: experience.company_name || "",
      work_hours_weekly: experience.work_hours_weekly || "",
      salary_range: experience.salary_range || "",
      company_description: experience.company_description || "",
      job_responsibilities: experience.job_responsibilties || "",
      benefits: experience.benefites || "",
      confidential: experience.confidetial || false,
      started_at: experience.started_at || "",
      ended_at: experience.ended_at || "",
      still_working: experience.still_working || false,
      country_id: experience.country_id || null,
      city_id: experience.city_id || null,
      category_id: experience.category_id || null,
      job_type_id: experience.job_type_id || null,
      currency: experience.currency || null,
    };
  } else {
    return {
      job_title: "",
      company_name: "",
      work_hours_weekly: "",
      salary_range: "",
      company_description: "",
      job_responsibilities: "",
      benefits: "",
      confidential: false,
      started_at: "",
      ended_at: "",
      still_working: false,
      country_id: null,
      city_id: null,
      category_id: null,
      job_type_id: null,
      currency: null,
    };
  }
};

const ExperienceSchema = Yup.object().shape({
  job_title: Yup.string().required("Job Title is required"),
  company_name: Yup.string().required("Company Name is required"),
  work_hours_weekly: Yup.number().required("Work Hours Weekly is required"),
  salary_range: Yup.number().required("Salary Range is required"),
  company_description: Yup.string(),
  job_responsibilities: Yup.string(),
  benefits: Yup.string(),
  confidential: Yup.boolean(),
  started_at: Yup.string().required("Start Date is required"),
  ended_at: Yup.string().required("End Date is required"),
  still_working: Yup.boolean(),
  country_id: Yup.number().required("Country is required"),
  city_id: Yup.number().required("City is required"),
  category_id: Yup.number().required("Category is required"),
  job_type_id: Yup.number().required("Job type is required"),
  currency: Yup.string().required("Currency is required"),
});

function AddEditResumeExperienceForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openLanguage: boolean) => void;
  item: ResumeExperience | null;
}) {
  const theme = useTheme();
  const intl = useIntl();

  // State for search terms
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [currencySearchTerm, setCurrencySearchTerm] = useState("");
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");

  // Debounced search terms
  const debouncedCategorySearchTerm = useDebounce(categorySearchTerm, 500);
  const debouncedCurrencySearchTerm = useDebounce(currencySearchTerm, 500);
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedJobTypeSearchTerm = useDebounce(jobTypeSearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);

  const {
    items: categories,
    loading: loadingCategories,
    hasMore: hasMoreCategories,
    loadMore: loadMoreCategories,
    error: categoriesError,
  } = useCategoriesForAutocomplete(20, debouncedCategorySearchTerm);

  const {
    items: currencies,
    loading: loadingCurrencies,
    hasMore: hasMoreCurrencies,
    loadMore: loadMoreCurrencies,
    error: currenciesError,
  } = useCurrenciesForAutocomplete(20, debouncedCurrencySearchTerm);

  const handleClose = () => {
    setOpen(false);
  };

  const formik = useFormik({
    initialValues: getInitialValues(item ?? null),
    validationSchema: ExperienceSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const experienceData = {
          ...values,
          resume_id: resumeId,
        };

        if (item && item?.id) {
          const response = await USERAXIOS.post(
            `resume/experiences/${item?.id}`,
            experienceData
          );
          const updatedExperience = response.data.data;

          if (updatedExperience) {
            setResult((prev) =>
              prev.map((item) =>
                item.id === updatedExperience.id ? updatedExperience : item
              )
            );

            openSnackbar({
              open: true,
              message: "Experience updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(
            "resume/experiences",
            experienceData
          );
          const newExperience = response.data.data;

          if (newExperience) {
            setResult((prev) => [...prev, newExperience]);

            openSnackbar({
              open: true,
              message: "Experience Added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        openSnackbar({
          open: true,
          message: "Something went wrong",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    handleChange,
  } = formik;

  const {
    items: jobTypes,
    loading: loadingJobTypes,
    hasMore: hasMoreJobTypes,
    loadMore: loadMoreJobTypes,
    error: jobTypesError,
  } = useJobTypesForAutocomplete(20, debouncedJobTypeSearchTerm);

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(
    formik.values.country_id,
    20,
    debouncedCitySearchTerm
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Experience" />
        ) : (
          <FormattedMessage id="Add Experience" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ flex: 1, overflowY: "auto" }}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="job-title">
                    <FormattedMessage id="job-title" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="job-title"
                    name="job_title"
                    value={values.job_title}
                    onChange={handleChange}
                    placeholder="Job Title"
                    error={touched.job_title && Boolean(errors.job_title)}
                  />
                  {touched.job_title && errors.job_title && (
                    <FormHelperText error>{errors.job_title}</FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="company-name">
                    <FormattedMessage id="company-name" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="company-name"
                    name="company_name"
                    value={values.company_name}
                    onChange={handleChange}
                    placeholder="Company Name"
                    error={touched.company_name && Boolean(errors.company_name)}
                  />
                  {touched.company_name && errors.company_name && (
                    <FormHelperText error>{errors.company_name}</FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="work-hours-weekly">
                    <FormattedMessage id="work-hours-weekly" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="work-hours-weekly"
                    name="work_hours_weekly"
                    value={values.work_hours_weekly}
                    onChange={handleChange}
                    placeholder="Work Hours Weekly"
                    type="number"
                    error={
                      touched.work_hours_weekly &&
                      Boolean(errors.work_hours_weekly)
                    }
                  />
                  {touched.work_hours_weekly && errors.work_hours_weekly && (
                    <FormHelperText error>
                      {errors.work_hours_weekly}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="started_at">Started At</InputLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        formik.values.started_at
                          ? dayjs(formik.values.started_at)
                          : null
                      }
                      onChange={(date) => {
                        const formattedDate = date
                          ? date.format("YYYY-MM-DD")
                          : "";
                        formik.setFieldValue("started_at", formattedDate);
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.started_at && formik.errors.started_at && (
                    <FormHelperText error>
                      {formik.errors.started_at}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="ended_at">Ended At</InputLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        formik.values.ended_at
                          ? dayjs(formik.values.ended_at)
                          : null
                      }
                      onChange={(date) => {
                        const formattedDate = date
                          ? date.format("YYYY-MM-DD")
                          : "";
                        formik.setFieldValue("ended_at", formattedDate);
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.ended_at && formik.errors.ended_at && (
                    <FormHelperText error>
                      {formik.errors.ended_at}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Country" />
                  </Typography>
                  <Autocomplete
                    id="country-select"
                    options={countries || []}
                    getOptionLabel={(option: Country) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      countries?.find(
                        (country) => country.id === values.country_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      const selectedCountryId = newValue?.id || null;

                      setFieldValue("country_id", selectedCountryId);
                      setFieldValue("city_id", null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCountrySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCountrySearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        error={touched.country_id && Boolean(errors.country_id)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCountries && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCountries &&
                          !loadingCountries
                        ) {
                          loadMoreCountries();
                        }
                      },
                    }}
                    loading={loadingCountries}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCountrySearchTerm.length >= 2
                        ? "No countries found"
                        : "Type to search countries"
                    }
                  />
                  {touched.country_id && errors.country_id && (
                    <FormHelperText error>{errors.country_id}</FormHelperText>
                  )}
                  {countriesError && (
                    <FormHelperText error>
                      Error loading countries. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="City" />
                  </Typography>
                  <Autocomplete
                    id="city-select"
                    options={cities}
                    getOptionLabel={(option: City) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      cities.find((city) => city.id === values.city_id) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("city_id", newValue?.id || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCitySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCitySearchTerm(newInputValue);
                      }
                    }}
                    disabled={!values.country_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select city"
                        error={touched.city_id && Boolean(errors.city_id)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCities && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCities &&
                          !loadingCities
                        ) {
                          loadMoreCities();
                        }
                      },
                    }}
                    loading={loadingCities}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCitySearchTerm.length >= 2
                        ? "No cities found"
                        : "Type to search cities"
                    }
                  />
                  {touched.city_id && errors.city_id && (
                    <FormHelperText error>{errors.city_id}</FormHelperText>
                  )}
                  {citiesError && (
                    <FormHelperText error>
                      Error loading cities. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Category" />
                  </Typography>
                  <Autocomplete
                    id="category-select"
                    options={categories}
                    getOptionLabel={(option: Category) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      categories.find(
                        (category) => category.id === values.category_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("category_id", newValue?.id || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCategorySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCategorySearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select category"
                        error={
                          touched.category_id && Boolean(errors.category_id)
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCategories && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCategories &&
                          !loadingCategories
                        ) {
                          loadMoreCategories();
                        }
                      },
                    }}
                    loading={loadingCategories}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCategorySearchTerm.length >= 2
                        ? "No categories found"
                        : "Type to search categories"
                    }
                  />
                  {touched.category_id && errors.category_id && (
                    <FormHelperText error>{errors.category_id}</FormHelperText>
                  )}
                  {categoriesError && (
                    <FormHelperText error>
                      Error loading categories. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Currency" />
                  </Typography>
                  <Autocomplete
                    id="currency-select"
                    options={currencies}
                    getOptionLabel={(option: Currency) =>
                      theme.direction === "rtl"
                        ? option.name_trans?.ar ?? option.name ?? ""
                        : option.name ?? ""
                    }
                    value={
                      currencies.find(
                        (currency) => currency.name === values.currency
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("currency", newValue?.name || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setCurrencySearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setCurrencySearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select currency"
                        error={touched.currency && Boolean(errors.currency)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCurrencies && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCurrencies &&
                          !loadingCurrencies
                        ) {
                          loadMoreCurrencies();
                        }
                      },
                    }}
                    loading={loadingCurrencies}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedCurrencySearchTerm.length >= 2
                        ? "No currencies found"
                        : "Type to search currencies"
                    }
                  />
                  {touched.currency && errors.currency && (
                    <FormHelperText error>{errors.currency}</FormHelperText>
                  )}
                  {currenciesError && (
                    <FormHelperText error>
                      Error loading currencies. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Category" />
                  </Typography>
                  <Autocomplete
                    id="category-select"
                    options={categories}
                    getOptionLabel={(option: Category) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    value={
                      categories.find(
                        (category) => category.id === values.category_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("category_id", newValue?.id || null);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setCategorySearchTerm(newInputValue);
                      // Load more when scrolling near the end
                      if (newInputValue.length > 2 && hasMoreCategories) {
                        loadMoreCategories();
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select category"
                        error={
                          touched.category_id && Boolean(errors.category_id)
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {categoriesLoading && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCategories &&
                          !categoriesLoading
                        ) {
                          loadMoreCategories();
                        }
                      },
                    }}
                    loading={categoriesLoading}
                  />

                  {touched.category_id && errors.category_id && (
                    <FormHelperText error>{errors.category_id}</FormHelperText>
                  )}
                </Stack>
              </Grid> */}
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Job Type" />
                  </Typography>
                  <Autocomplete
                    id="job-type-select"
                    options={jobTypes}
                    getOptionLabel={(option: JobType) =>
                      theme.direction === "rtl"
                        ? option?.name_trans?.ar
                        : option.name
                    }
                    value={
                      jobTypes.find(
                        (jobType) => jobType.id === values.job_type_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("job_type_id", newValue?.id || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setJobTypeSearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setJobTypeSearchTerm(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select job type"
                        error={
                          touched.job_type_id && Boolean(errors.job_type_id)
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingJobTypes && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreJobTypes &&
                          !loadingJobTypes
                        ) {
                          loadMoreJobTypes();
                        }
                      },
                    }}
                    loading={loadingJobTypes}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      debouncedJobTypeSearchTerm.length >= 2
                        ? "No job types found"
                        : "Type to search job types"
                    }
                  />
                  {touched.job_type_id && errors.job_type_id && (
                    <FormHelperText error>{errors.job_type_id}</FormHelperText>
                  )}
                  {jobTypesError && (
                    <FormHelperText error>
                      Error loading job types. Please try again.
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="salary-range">
                    <FormattedMessage id="Net salary" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="salary-range"
                    name="salary_range"
                    value={values.salary_range}
                    onChange={handleChange}
                    placeholder="Net salary"
                    type="number"
                    error={touched.salary_range && Boolean(errors.salary_range)}
                  />
                  {touched.salary_range && errors.salary_range && (
                    <FormHelperText error>{errors.salary_range}</FormHelperText>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Currency" />
                  </Typography>
                  <Autocomplete
                    id="currency-select"
                    options={currencies}
                    getOptionLabel={(option: Currency) =>
                      theme.direction === "rtl"
                        ? option.name_trans?.ar ?? option.name ?? ""
                        : option.name ?? ""
                    }
                    value={
                      currencies.find(
                        (currency) => currency.name === values.currency
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("currency", newValue?.name || null);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setCurrencySearchTerm(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select currency"
                        error={touched.currency && Boolean(errors.currency)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCurrencies && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreCurrencies &&
                          !loadingCurrencies
                        ) {
                          loadMoreCurrencies();
                        }
                      },
                    }}
                    loading={loadingCurrencies}
                  />
                  {touched.currency && errors.currency && (
                    <FormHelperText error>{errors.currency}</FormHelperText>
                  )}
                </Stack>
              </Grid> */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.confidential}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "confidential",
                            event.target.checked
                          );
                        }}
                        id="confidential-switch"
                      />
                    }
                    label="Confidential"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="company-description">
                    Company Description
                  </InputLabel>
                  <TextArea
                    rows={4}
                    id="company-description"
                    value={values.company_description}
                    name="company_description"
                    onChange={handleChange}
                    placeholder={intl.formatMessage({
                      id: "company-description",
                      defaultMessage: "Company Description",
                    })}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="job-responsibilities">
                    Job Responsibilities
                  </InputLabel>
                  <TextArea
                    rows={4}
                    id="job-responsibilities"
                    value={values.job_responsibilities}
                    name="job_responsibilities"
                    onChange={handleChange}
                    placeholder={intl.formatMessage({
                      id: "job-responsibilities",
                      defaultMessage: "Job Responsibilities",
                    })}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="benefits">Benefits</InputLabel>
                  <TextArea
                    rows={4}
                    id="benefits"
                    value={values.benefits}
                    name="benefits"
                    onChange={handleChange}
                    placeholder={intl.formatMessage({
                      id: "benefits",
                      defaultMessage: "Benefits",
                    })}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.still_working}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "still_working",
                            event.target.checked
                          );
                        }}
                        id="stillWorking-switch"
                      />
                    }
                    label="Still Working"
                  />
                </Box>
              </Grid>
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditResumeExperienceForm;
